import { AHText, Box, TouchableBox } from "arkh";
import { graphql, useFragment } from "react-relay";
import { TimeSeriesOverviewItem_data$key } from "./__generated__/TimeSeriesOverviewItem_data.graphql";
import { StyleSheet } from "react-native";
import { useFormatValueUnit } from "./formatDuration";
import React, { useMemo } from "react";

export function TimeSeriesOverviewItem(props: {
  data: TimeSeriesOverviewItem_data$key | null;
  title: string;
  subTitle?: string;
  onPress?: () => void;
  mode?: "total" | "avg";
  trendMode?: "small-is-better" | "large-is-better" | "none";
}) {
  const trendMode = props.trendMode ?? "large-is-better";
  const data = useFragment(
    graphql`
      fragment TimeSeriesOverviewItem_data on WorkspaceTimeSeriesAnalytics {
        valueUnit
        connection(
          first: 2
          skip: 1
          groupByDimension: $groupByDimension
          timeResolution: $timeResolution
        ) {
          edges {
            node {
              formattedDate
              total
              avg
            }
          }
        }
      }
    `,
    props.data
  );
  const mode = props.mode ?? "total";

  const formatValue = useFormatValueUnit();
  const current = data?.connection.edges[0].node[mode] ?? 0;
  const previous = data?.connection.edges[1].node[mode] ?? 0;

  const type = useMemo(() => {
    if (trendMode === "large-is-better") {
      return current > previous ? ("positive" as const) : ("negative" as const);
    }
    if (trendMode === "small-is-better") {
      return current < previous ? ("positive" as const) : ("negative" as const);
    }
    return "neutral" as const;
  }, [current, previous, trendMode]);
  if (!data) {
    return null;
  }

  const trend = {
    label:
      `vs ${formatValue(previous, data.valueUnit)} in ` +
      data.connection.edges[1].node.formattedDate,
    change:
      previous > 0
        ? `${current > previous ? "+" : ""}${Math.round(((current - previous) / previous) * 100)}%`
        : "%",
  };
  return (
    <TouchableBox
      color="surface-primary"
      style={{ width: 240, borderWidth: 1 }}
      cornerRadius="large"
      borderColor="border-primary"
      space="large"
      onPress={props.onPress}
    >
      <Box style={{ flexDirection: "row" }}>
        <Box>
          <AHText type="subtitle" numberOfLines={1}>
            {props.title}
          </AHText>
          {props.subTitle ? (
            <AHText type="detail" color="text-secondary">
              {props.subTitle}
            </AHText>
          ) : null}
        </Box>
      </Box>

      <Box style={{ flex: 1 }} spaceTop="small">
        <Box
          style={{
            flexDirection: "row",
            alignItems: "baseline",
            flexWrap: "wrap",
            overflow: "hidden",
          }}
        >
          <AHText
            type="title-big"
            style={{ maxWidth: "100%" }}
            numberOfLines={1}
            adjustsFontSizeToFit
          >
            {formatValue(current, data.valueUnit)}
          </AHText>
        </Box>

        <Box
          spaceTop="small"
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {previous === 0 ? null : (
            <React.Fragment>
              <Box spaceRight="xsmall">
                <Box
                  cornerRadius="full"
                  spaceHorizontal="small"
                  spaceVertical="xsmall"
                  style={{ position: "relative", overflow: "hidden" }}
                >
                  <AHText type="subtitle" color={type}>
                    {trend.change}
                  </AHText>
                  <Box
                    style={[StyleSheet.absoluteFill, { opacity: 0.1 }]}
                    color={type}
                  />
                </Box>
              </Box>
              <AHText spaceLeft="xsmall" type="detail">
                {trend.label}
              </AHText>
            </React.Fragment>
          )}
        </Box>
      </Box>
    </TouchableBox>
  );
}
